import { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { Scissors, Flame, Loader2, Search, PlayCircle, AlertTriangle, HeartHandshake, ListVideo, BookOpen, ExternalLink, Church, Share2, Phone, LifeBuoy, Sparkles, Clock, Zap, Play, Instagram, Youtube, Twitter, Facebook, Globe, Heart, MessageCircle, Trophy } from "lucide-react";
import { getPublicCreator, getPublicCare, getPublicResources, publicSearch, getPublicMoments, shareLink, mediaUrl, streamUrl } from "../lib/api";
import { toast } from "sonner";

// When served on a creator's own custom domain (e.g. barthandfriends.com), present a clean
// audience site — hide the Clip Miner product pitch (that lives on clip-miner.com for creators).
const isBrandedHost = () =>
  !/clip-miner|emergent|localhost|127\.0\.0\.1|vercel|netlify/i.test(window.location.hostname);

// Convert a #RRGGBB hex to the "H S% L%" triplet Tailwind's --accent var expects.
function hexToHslTriplet(hex) {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex || "");
  if (!m) return null;
  const n = parseInt(m[1], 16);
  const r = ((n >> 16) & 255) / 255, g = ((n >> 8) & 255) / 255, b = (n & 255) / 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  let h = 0, s = 0; const l = (max + min) / 2;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    if (max === r) h = (g - b) / d + (g < b ? 6 : 0);
    else if (max === g) h = (b - r) / d + 2;
    else h = (r - g) / d + 4;
    h /= 6;
  }
  return `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
}

export function ShareBtn({ url, label, className = "" }) {
  const doShare = async (e) => {
    e.preventDefault(); e.stopPropagation();
    const full = url.startsWith("http") ? url : window.location.origin + url;
    try { if (navigator.share) { await navigator.share({ url: full, title: label }); return; } } catch { /* fall through */ }
    try { await navigator.clipboard.writeText(full); toast.success("Share link copied!"); }
    catch { window.prompt("Copy this link:", full); }
  };
  return (
    <button data-testid="share-btn" onClick={doShare}
            className={`inline-flex items-center gap-1 font-mono text-[11px] text-accent transition-colors hover:text-white ${className}`}>
      <Share2 className="h-3.5 w-3.5" /> Share
    </button>
  );
}

export const ts = (s) => `${Math.floor((s || 0) / 60)}:${String(Math.floor((s || 0) % 60)).padStart(2, "0")}`;

export const ISSUES = [
  "addiction", "alcoholism", "recovery", "divorce", "marriage", "dating",
  "domestic violence", "grief", "anxiety", "depression", "mental health", "parenting",
  "family", "faith", "forgiveness", "loneliness", "anger", "finances", "purpose", "trauma",
];

export default function CreatorSite({ forcedHandle }) {
  const params = useParams();
  const handle = forcedHandle || params.handle;
  const [data, setData] = useState(null);
  const [state, setState] = useState("loading");
  const [careTopics, setCareTopics] = useState([]);
  const [q, setQ] = useState("");
  const [topic, setTopic] = useState(null);
  const [results, setResults] = useState(null);
  const [searching, setSearching] = useState(false);
  const [moments, setMoments] = useState(null);

  useEffect(() => {
    getPublicCreator(handle)
      .then((d) => { setData(d); setState("ready"); })
      .catch(() => setState("error"));
    getPublicCare(handle).then((d) => setCareTopics(d.topics || [])).catch(() => {});
    getPublicMoments(handle).then(setMoments).catch(() => {});
    // deep links: /u/handle?issue=addiction  or  ?tab=faith|stories
    const params = new URLSearchParams(window.location.search);
    const issue = params.get("issue");
    const tab = params.get("tab");
    if (tab === "faith" || tab === "stories") setTopic(`__${tab}__`);
    else if (issue) { setTopic(issue); runSearch("", issue); }
  }, [handle]); // eslint-disable-line react-hooks/exhaustive-deps

  const runSearch = useCallback((query, top) => {
    if (!query && !top) { setResults(null); return; }
    setSearching(true);
    publicSearch(handle, { q: query || undefined, topic: top || undefined })
      .then((d) => setResults(d))
      .catch(() => setResults({ clips: [], care: [], chapters: [], resources: [], counts: {} }))
      .finally(() => setSearching(false));
  }, [handle]);

  const pickTopic = (t) => {
    const nt = t === topic ? null : t;
    setTopic(nt);
    if (nt && nt.startsWith("__")) { setResults(null); setQ(""); return; }
    runSearch(q.trim(), nt);
  };
  const submit = () => { if (topic && topic.startsWith("__")) setTopic(null); runSearch(q.trim(), topic && topic.startsWith("__") ? null : topic); };
  const showMoments = (kind) => { setTopic(`__${kind}__`); setResults(null); setQ(""); window.scrollTo({ top: 320, behavior: "smooth" }); };

  if (state === "loading")
    return <div className="grid h-screen place-items-center bg-background text-accent"><Loader2 className="h-8 w-8 animate-spin" /></div>;
  if (state === "error")
    return (
      <div className="grid h-screen place-items-center bg-background text-center text-white">
        <div>
          <AlertTriangle className="mx-auto h-8 w-8 text-primary" />
          <p className="mt-4 font-display text-2xl">Creator page not found</p>
          <p className="mt-2 font-body text-sm text-muted-foreground">This page may be private or the link is wrong.</p>
          <a href="/demo" className="mt-6 inline-block bg-accent px-6 py-3 font-body text-sm font-semibold text-black">See the live demo</a>
        </div>
      </div>
    );

  const c = data.creator;
  const active = q.trim() || topic;
  const brandedMode = isBrandedHost();
  const accentTriplet = hexToHslTriplet(c.accent_color);
  const rootStyle = accentTriplet ? { "--accent": accentTriplet } : undefined;

  return (
    <div className="min-h-screen bg-background text-white" data-testid="creator-site" style={rootStyle}>
      <header className="border-b border-border">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-6 py-4">
          <a href={brandedMode ? "/" : "/demo"} className="flex items-center gap-3">
            <div className="grid h-9 w-9 place-items-center bg-accent"><Scissors className="h-4 w-4 text-black" /></div>
            <span className="font-mono text-xs uppercase tracking-[0.3em] text-accent">{brandedMode ? (c.name || c.handle) : "Clip Miner"}</span>
          </a>
          {!brandedMode && <a href="/" className="bg-accent px-4 py-2 font-body text-xs font-semibold text-black transition-transform hover:-translate-y-0.5">Try it free</a>}
        </div>
      </header>

      {/* Hero */}
      <section className="border-b border-border">
        <div className="mx-auto flex max-w-6xl flex-col gap-5 px-6 py-12 sm:flex-row sm:items-center">
          {c.picture ? (
            <img src={c.picture} alt={c.name} className="h-24 w-24 rounded-full border-2 border-accent object-cover" />
          ) : (
            <div className="grid h-24 w-24 place-items-center rounded-full border-2 border-accent bg-accent/10 font-display text-3xl text-accent">
              {(c.name || c.handle || "?").slice(0, 1).toUpperCase()}
            </div>
          )}
          <div>
            <p className="font-mono text-xs uppercase tracking-[0.3em] text-accent">@{c.handle}</p>
            <h1 className="mt-1 font-display text-4xl tracking-wide sm:text-5xl" data-testid="creator-name">{c.name || c.handle}</h1>
            {c.bio && <p className="mt-2 max-w-xl font-body text-sm text-muted-foreground">{c.bio}</p>}
            <p className="mt-3 font-mono text-xs text-muted-foreground">
              {data.shows.length} shows · {data.shows.reduce((a, s) => a + (s.clip_count || 0), 0)} clips
            </p>
          </div>
        </div>
      </section>

      {/* Social / community / tip links */}
      <CreatorLinks c={c} handle={handle} />

      {/* Creator hook — makes a visiting creator instantly "get it" */}
      {!brandedMode && <CreatorHook data={data} />}

      {/* YANA prompt — the intriguing doorway to the deep-dive help hub */}
      <section className="mx-auto max-w-6xl px-6 pt-8">
        <Link to={`/u/${handle}/yana`} data-testid="yana-cta"
              className="group flex flex-col items-start justify-between gap-4 border border-primary/40 bg-primary/[0.06] p-6 transition-colors hover:border-primary sm:flex-row sm:items-center">
          <div>
            <p className="flex items-center gap-2 font-display text-2xl tracking-wide text-primary"><LifeBuoy className="h-6 w-6" /> Struggling with something?</p>
            <p className="mt-1 max-w-2xl font-body text-sm text-muted-foreground">
              You're not alone. Step into <span className="font-semibold text-white">YANA</span> — real testimonies, honest laughs, where Jesus shows up, help lines, and people who've been there and want to walk with you. Pick what you're facing and dive as deep as you want.
            </p>
          </div>
          <span className="shrink-0 bg-primary px-6 py-3 font-body text-sm font-semibold text-white transition-transform group-hover:translate-x-1">Enter YANA →</span>
        </Link>
      </section>

      {/* Funny is the star: search + daily show catalog */}
      <section className="mx-auto max-w-6xl px-6 py-8">
        <p className="font-display text-2xl tracking-wide">The funniest moments, on tap</p>
        <p className="mt-1 font-body text-sm text-muted-foreground">Browse the daily show, or search a bit, a name, or a vibe.</p>
        <div className="relative mt-4 max-w-2xl">
          <Search className="pointer-events-none absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-muted-foreground" />
          <input
            data-testid="creator-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && submit()}
            placeholder="Search “crowd work”, “funniest moment”, a heckler bit…"
            className="w-full rounded-none border border-border bg-card py-3.5 pl-12 pr-28 font-body text-base text-white outline-none focus:border-accent"
          />
          <button data-testid="creator-search-btn" onClick={submit}
                  className="absolute right-1.5 top-1/2 -translate-y-1/2 bg-accent px-4 py-2 font-body text-xs font-semibold text-black">
            Search
          </button>
        </div>
      </section>

      {/* Hall of Fame — creator's hand-picked clips */}
      {!active && data.featured_clips?.length > 0 && (
        <section className="mx-auto max-w-6xl px-6 pt-4" data-testid="hall-of-fame">
          <div className="flex items-center gap-2">
            <Trophy className="h-5 w-5 text-accent" />
            <h2 className="font-display text-2xl tracking-wide text-accent">Hall of Fame</h2>
          </div>
          <p className="mt-1 font-body text-sm text-muted-foreground">Hand-picked highlights — the ones worth starting with.</p>
          <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
            {data.featured_clips.map((clip) => <ClipTile key={clip.id} clip={clip} />)}
          </div>
        </section>
      )}

      {/* Results OR moments OR default browse */}
      <section className="mx-auto max-w-6xl px-6 pb-16">
        {topic === "__faith__" ? (
          <MomentsView kind="faith" moments={moments} handle={handle} />
        ) : topic === "__stories__" ? (
          <MomentsView kind="stories" moments={moments} handle={handle} />
        ) : searching ? (
          <div className="flex h-40 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-accent" /></div>
        ) : active && results ? (
          <UnifiedResults results={results} query={q.trim()} topic={topic} handle={handle} />
        ) : (
          <DefaultBrowse data={data} />
        )}
      </section>

      <footer className="border-t border-border py-10 text-center">
        {brandedMode ? (
          <p className="font-body text-sm text-muted-foreground">© {new Date().getFullYear()} {c.name || c.handle} — every show in one place.</p>
        ) : (
          <>
            <p className="font-body text-sm text-muted-foreground">Powered by <span className="text-accent">Clip Miner</span> — turn your shows into clips automatically.</p>
            <a href="/" className="mt-4 inline-block bg-accent px-6 py-3 font-body text-sm font-semibold text-black">Start free</a>
          </>
        )}
      </footer>
    </div>
  );
}

function CreatorHook({ data }) {
  const shows = (data.shows || []).length;
  const clips = (data.shows || []).reduce((a, s) => a + (s.clip_count || 0), 0);
  const secs = (data.shows || []).reduce((a, s) => a + (s.duration_sec || 0), 0);
  const hours = Math.max(1, Math.round(secs / 3600));
  return (
    <section className="mx-auto max-w-6xl px-6 pt-10" data-testid="creator-hook">
      <div className="relative overflow-hidden border border-accent/40 bg-accent/[0.04] p-6 sm:p-8">
        <div className="pointer-events-none absolute -right-16 -top-16 h-48 w-48 rounded-full bg-accent/10 blur-3xl" />
        <p className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.3em] text-accent">
          <Sparkles className="h-4 w-4" /> Made automatically with Clip Miner
        </p>
        <h2 className="mt-3 max-w-3xl font-display text-3xl leading-tight tracking-wide sm:text-4xl">
          Every clip, chapter and searchable moment on this page was cut{" "}
          <span className="text-accent">automatically</span> — no editing, no scrubbing.
        </h2>
        <p className="mt-3 max-w-2xl font-body text-sm text-muted-foreground sm:text-base">
          Clip Miner turned <span className="font-semibold text-white">{hours} hours</span> of raw show into{" "}
          <span className="font-semibold text-white">{clips} ready-to-post clips</span> across{" "}
          <span className="font-semibold text-white">{shows} episodes</span> — captioned, reframed, tagged, and searchable by
          topic, name or vibe. What used to take days of editing now happens on upload.
        </p>
        <div className="mt-5 grid gap-3 sm:grid-cols-3">
          {[
            { icon: Clock, big: `${hours} hrs`, small: "of footage mined — hands-off" },
            { icon: Scissors, big: `${clips} clips`, small: "captioned & reframed, zero manual edits" },
            { icon: Zap, big: "Every word", small: "searchable in seconds, not hours" },
          ].map((s, i) => (
            <div key={i} className="flex items-center gap-3 border border-border bg-background/50 px-4 py-3">
              <s.icon className="h-5 w-5 shrink-0 text-accent" />
              <div>
                <p className="font-display text-lg leading-none tracking-wide">{s.big}</p>
                <p className="mt-1 font-body text-xs text-muted-foreground">{s.small}</p>
              </div>
            </div>
          ))}
        </div>
        <div className="mt-6 flex flex-wrap items-center gap-3">
          <a href="/" data-testid="creator-hook-cta"
             className="bg-accent px-6 py-3 font-body text-sm font-semibold text-black transition-transform hover:-translate-y-0.5">
            Turn your show into this — free →
          </a>
          <a href="/demo" data-testid="creator-hook-demo"
             className="font-body text-sm text-muted-foreground underline-offset-4 transition-colors hover:text-accent hover:underline">
            See how it works
          </a>
        </div>
      </div>
    </section>
  );
}

function SectionHead({ icon: Icon, title, count }) {
  return (
    <h2 className="flex items-center gap-2 font-display text-2xl tracking-wide">
      <Icon className="h-5 w-5 text-accent" /> {title}
      {count != null && <span className="font-mono text-xs text-muted-foreground">({count})</span>}
    </h2>
  );
}

function ClipTile({ clip }) {
  return (
    <Link to={`/c/${clip.id}`} data-testid={`clip-card-${clip.id}`}
          className="group relative block overflow-hidden border border-border bg-black transition-transform hover:-translate-y-1 hover:border-accent">
      <div className="relative aspect-[9/16] w-full bg-neutral-900">
        {clip.poster ? (
          <img src={mediaUrl(clip.poster)} alt={clip.title} loading="lazy" className="h-full w-full object-cover" />
        ) : (
          <>
            <img src="/barth-tile.jpg" alt={clip.title} loading="lazy" className="h-full w-full object-cover" />
            <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/85 to-transparent p-2.5 pt-8 text-center font-display text-xs text-white">{clip.title}</div>
          </>
        )}
        <div className="absolute inset-0 flex items-center justify-center opacity-0 transition-opacity group-hover:bg-black/30 group-hover:opacity-100"><PlayCircle className="h-10 w-10 text-accent" /></div>
        <span className="absolute left-2 top-2 flex items-center gap-1 bg-accent px-1.5 py-0.5 font-mono text-[10px] font-bold text-black"><Flame className="h-2.5 w-2.5" /> {clip.funny_score}</span>
      </div>
      <div className="p-2.5">
        <p className="line-clamp-2 font-body text-xs text-white">{clip.title}</p>
        {clip.topics?.length > 0 && (
          <p className="mt-1 line-clamp-1 font-mono text-[10px] text-accent">{clip.topics.map((t) => `#${t.replace(/\s+/g, "")}`).join(" ")}</p>
        )}
      </div>
    </Link>
  );
}

export function UnifiedResults({ results, query, topic, handle }) {
  const { clips = [], care = [], chapters = [], resources = [], stories = [], faith = [] } = results;
  const empty = !clips.length && !care.length && !chapters.length && !resources.length && !stories.length && !faith.length;
  const label = query || topic;
  const helplines = resources.filter((r) => r.kind === "helpline");
  const sermons = resources.filter((r) => r.kind !== "helpline");
  if (empty)
    return <p className="py-16 text-center font-body text-sm text-muted-foreground">Nothing found for “{label}”. Try another word.</p>;
  return (
    <div className="space-y-12" data-testid="unified-results">
      <div className="flex items-center justify-between">
        <p className="font-mono text-xs uppercase tracking-widest text-muted-foreground">Results for “{label}”</p>
        {topic && !topic.startsWith("__") && handle && (
          <ShareBtn url={shareLink.topic(handle, topic)} label={`${topic} — Barth & Friends`} />
        )}
      </div>

      {helplines.length > 0 && (
        <div className="border border-primary/40 bg-primary/[0.06] p-5" data-testid="get-help">
          <h2 className="flex items-center gap-2 font-display text-xl tracking-wide text-primary"><LifeBuoy className="h-5 w-5" /> Get help now — you're not alone</h2>
          <div className="mt-3 grid gap-2 sm:grid-cols-2">
            {helplines.map((r) => (
              <a key={r.id} href={r.url} target="_blank" rel="noreferrer" data-testid={`helpline-${r.id}`}
                 className="flex items-start gap-3 border border-primary/30 bg-background/40 px-4 py-3 transition-colors hover:border-primary">
                <Phone className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                <span>
                  <span className="block font-body text-sm font-semibold">{r.title}</span>
                  <span className="block font-body text-xs text-muted-foreground">{r.series}</span>
                </span>
              </a>
            ))}
          </div>
        </div>
      )}

      {clips.length > 0 && (
        <div>
          <SectionHead icon={Flame} title="Clips" count={clips.length} />
          <p className="mt-1 font-body text-xs text-muted-foreground">Watchable moments about “{label}”.</p>
          <div className="mt-3 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4" data-testid="results-clips">
            {clips.map((clip) => <ClipTile key={clip.id} clip={clip} />)}
          </div>
        </div>
      )}

      {faith.length > 0 && (
        <div>
          <SectionHead icon={Church} title="Faith & Jesus" count={faith.length} />
          <p className="mt-1 font-body text-xs text-muted-foreground">Every moment Barth talks about his faith.</p>
          <div className="mt-3 space-y-2" data-testid="results-faith">
            {faith.map((e, i) => (
              <div key={e.id || i} className="border border-border transition-colors hover:border-accent hover:bg-accent/[0.06]">
                <Link to={`/s/${e.show_id}?t=${Math.floor(e.start)}`} data-testid={`faith-result-${i}`} className="block px-4 py-3">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-body text-sm font-semibold">{e.title}</span>
                    <span className="font-mono text-[10px] text-muted-foreground">{ts(e.start)} · {e.show_title}</span>
                  </div>
                  {e.caption && <p className="mt-1 font-body text-xs text-muted-foreground">{e.caption}</p>}
                </Link>
                <div className="px-4 pb-2"><ShareBtn url={shareLink.show(e.show_id, e.start)} label={e.title} /></div>
              </div>
            ))}
          </div>
        </div>
      )}

      {stories.length > 0 && (
        <div>
          <SectionHead icon={BookOpen} title="Barth's Stories" count={stories.length} />
          <p className="mt-1 font-body text-xs text-muted-foreground">Stories from Barth's own life.</p>
          <div className="mt-3 space-y-2" data-testid="results-stories">
            {stories.map((e, i) => (
              <div key={e.id || i} className="border border-border transition-colors hover:border-accent hover:bg-accent/[0.06]">
                <Link to={`/s/${e.show_id}?t=${Math.floor(e.start)}`} data-testid={`story-result-${i}`} className="block px-4 py-3">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-body text-sm font-semibold">{e.title}</span>
                    <span className="font-mono text-[10px] text-muted-foreground">{ts(e.start)} · {e.show_title}</span>
                  </div>
                  {e.caption && <p className="mt-1 font-body text-xs text-muted-foreground">{e.caption}</p>}
                </Link>
                <div className="px-4 pb-2"><ShareBtn url={shareLink.show(e.show_id, e.start)} label={e.title} /></div>
              </div>
            ))}
          </div>
        </div>
      )}

      {care.length > 0 && (
        <div>
          <SectionHead icon={HeartHandshake} title="Prayer & Care" count={care.length} />
          <p className="mt-1 font-body text-xs text-muted-foreground">People who shared what they're walking through.</p>
          <div className="mt-3 space-y-2" data-testid="results-care">
            {care.map((e, i) => (
              <Link key={e.id || i} to={`/s/${e.show_id}`} data-testid={`care-result-${i}`}
                    className="block border border-border px-4 py-3 transition-colors hover:border-primary hover:bg-primary/[0.05]">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-body text-sm font-semibold">{e.person || "Anonymous"}</span>
                  {e.topic && <span className="bg-primary/15 px-1.5 py-0.5 font-mono text-[10px] uppercase tracking-wider text-primary">{e.topic}</span>}
                  <span className="font-mono text-[10px] text-muted-foreground">{ts(e.start)} · {e.show_title}</span>
                </div>
                {e.summary && <p className="mt-1 font-body text-xs text-muted-foreground">{e.summary}</p>}
              </Link>
            ))}
          </div>
        </div>
      )}

      {sermons.length > 0 && (
        <div>
          <SectionHead icon={BookOpen} title="Sermons & Resources" count={sermons.length} />
          <p className="mt-1 font-body text-xs text-muted-foreground">Helpful teaching on this topic.</p>
          <div className="mt-3 grid gap-2 sm:grid-cols-2" data-testid="results-resources">
            {sermons.map((r) => (
              <a key={r.id} href={r.url} target="_blank" rel="noreferrer" data-testid={`resource-${r.id}`}
                 className="flex items-start justify-between gap-3 border border-border px-4 py-3 transition-colors hover:border-accent">
                <div>
                  <p className="font-body text-sm font-semibold">{r.title}</p>
                  <p className="mt-1 font-mono text-[10px] text-muted-foreground">
                    {r.source || "Resource"}{r.series ? ` · ${r.series}` : ""}
                  </p>
                </div>
                <ExternalLink className="mt-0.5 h-4 w-4 shrink-0 text-accent" />
              </a>
            ))}
          </div>
        </div>
      )}

      {chapters.length > 0 && (
        <div>
          <SectionHead icon={ListVideo} title="Episode Chapters" count={chapters.length} />
          <div className="mt-3 divide-y divide-border border border-border" data-testid="results-chapters">
            {chapters.map((ch, i) => (
              <Link key={i} to={`/s/${ch.show_id}`} className="flex w-full items-start gap-4 px-4 py-3 transition-colors hover:bg-accent/[0.06]">
                <span className="mt-0.5 shrink-0 font-mono text-sm text-accent">{ts(ch.start)}</span>
                <span className="min-w-0">
                  <span className="block font-body text-sm font-semibold">{ch.title}</span>
                  {ch.caption && <span className="block font-body text-xs text-muted-foreground">{ch.caption} · {ch.show_title}</span>}
                </span>
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export function MomentsView({ kind, moments, handle }) {
  if (!moments) return <div className="flex h-40 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-accent" /></div>;
  const list = (kind === "faith" ? moments.faith : moments.stories) || [];
  const Icon = kind === "faith" ? Church : BookOpen;
  const title = kind === "faith" ? "Faith & Jesus" : "Barth's Stories";
  const sub = kind === "faith"
    ? "Every moment across the shows where Barth talks about Jesus, prayer and his faith."
    : "Real stories from Barth's own life and past, pulled from across the shows.";
  if (!list.length)
    return <p className="py-16 text-center font-body text-sm text-muted-foreground">These are still being pulled from the recordings — check back shortly.</p>;
  const byShow = {};
  list.forEach((m) => { (byShow[m.show_id] = byShow[m.show_id] || { title: m.show_title, items: [] }).items.push(m); });
  return (
    <div data-testid={`moments-${kind}`}>
      <div className="flex items-center justify-between">
        <SectionHead icon={Icon} title={title} count={list.length} />
        {handle && <ShareBtn url={shareLink.topic(handle, kind === "faith" ? "faith" : "family")} label={title} />}
      </div>
      <p className="mt-1 font-body text-sm text-muted-foreground">{sub}</p>
      <div className="mt-5 space-y-8">
        {Object.entries(byShow).map(([sid, grp]) => (
          <div key={sid}>
            <Link to={`/s/${sid}`} className="font-mono text-xs uppercase tracking-widest text-accent hover:underline">{grp.title}</Link>
            <div className="mt-2 space-y-2">
              {grp.items.map((e, i) => (
                <div key={e.id || i} className="border border-border transition-colors hover:border-accent hover:bg-accent/[0.06]">
                  <Link to={`/s/${sid}?t=${Math.floor(e.start)}`} data-testid={`${kind}-moment-${i}`} className="block px-4 py-3">
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-xs text-accent">{ts(e.start)}</span>
                      <span className="font-body text-sm font-semibold">{e.title}</span>
                    </div>
                    {e.caption && <p className="mt-1 font-body text-xs text-muted-foreground">{e.caption}</p>}
                    {e.quote && <p className="mt-1 font-body text-xs italic text-muted-foreground">“{e.quote}”</p>}
                  </Link>
                  <div className="px-4 pb-2"><ShareBtn url={shareLink.show(e.show_id, e.start)} label={e.title} /></div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

const SOCIAL_ICONS = { instagram: Instagram, youtube: Youtube, x: Twitter, facebook: Facebook, website: Globe };

function tipLinks(c) {
  const out = [];
  if (c.tip_venmo) out.push({ id: "venmo", label: "Venmo", href: `https://venmo.com/u/${c.tip_venmo}` });
  if (c.tip_cashapp) out.push({ id: "cashapp", label: "Cash App", href: `https://cash.app/$${c.tip_cashapp}` });
  if (c.tip_paypal) {
    let h = c.tip_paypal;
    if (!/^https?:/i.test(h)) h = h.includes("paypal") ? `https://${h}` : `https://paypal.me/${h}`;
    out.push({ id: "paypal", label: "PayPal", href: h });
  }
  return out;
}

function CreatorLinks({ c }) {
  const socials = Object.entries(c.socials || {}).filter(([, v]) => v);
  const tips = tipLinks(c);
  if (!socials.length && !c.discord_url && !tips.length) return null;
  return (
    <section className="border-b border-border bg-black/30" data-testid="creator-links">
      <div className="mx-auto flex max-w-6xl flex-wrap items-center gap-3 px-6 py-4">
        {socials.map(([k, v]) => {
          const Icon = SOCIAL_ICONS[k] || Globe;
          return (
            <a key={k} href={v} target="_blank" rel="noopener noreferrer" data-testid={`social-${k}`} title={k}
               className="grid h-10 w-10 place-items-center border border-border text-muted-foreground transition-colors hover:border-accent hover:text-accent">
              <Icon className="h-4 w-4" />
            </a>
          );
        })}
        {c.discord_url && (
          <a href={c.discord_url} target="_blank" rel="noopener noreferrer" data-testid="discord-link"
             className="inline-flex items-center gap-2 border border-border px-3 py-2 font-body text-xs text-muted-foreground transition-colors hover:border-accent hover:text-accent">
            <MessageCircle className="h-4 w-4" /> Join the Discord
          </a>
        )}
        {tips.length > 0 && (
          <div className="ml-auto flex flex-wrap items-center gap-2" data-testid="tip-jar">
            <span className="inline-flex items-center gap-1 font-mono text-[11px] uppercase tracking-widest text-accent"><Heart className="h-3.5 w-3.5" /> Tip jar</span>
            {tips.map((t) => (
              <a key={t.label} href={t.href} target="_blank" rel="noopener noreferrer"
                 data-testid={`tip-${t.id}`}
                 className="border border-accent/40 bg-accent/10 px-3 py-2 font-body text-xs font-semibold text-accent transition-colors hover:bg-accent hover:text-black">
                {t.label}
              </a>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

function fmtDate(iso) {
  try { return new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" }); }
  catch { return ""; }
}

function EpisodeCard({ s, featured }) {
  const [preview, setPreview] = useState(false);
  const tags = Array.from(new Set([...(s.care_topics || []), ...(s.topics || [])])).slice(0, 6);
  return (
    <div data-testid={`show-card-${s.id}`}
         className={`border p-5 transition-colors ${featured ? "border-accent/40 bg-accent/[0.04]" : "border-border"}`}>
      {s.trailer_path && (
        <div className="mb-3">
          {preview ? (
            <video src={streamUrl(s.trailer_path)} controls autoPlay playsInline
                   className="aspect-video w-full border border-accent/40 bg-black" data-testid={`trailer-preview-${s.id}`} />
          ) : (
            <button type="button" onClick={() => setPreview(true)} data-testid={`trailer-thumb-${s.id}`}
                    className="group relative flex aspect-video w-full items-center justify-center border border-accent/30 bg-gradient-to-br from-black to-zinc-900 transition-colors hover:border-accent">
              <span className="flex flex-col items-center gap-2 font-mono text-[11px] uppercase tracking-widest text-accent">
                <span className="grid h-12 w-12 place-items-center rounded-full bg-accent text-black transition-transform group-hover:scale-110"><Play className="h-5 w-5" fill="currentColor" /></span>
                Preview · 60s best-of
              </span>
            </button>
          )}
        </div>
      )}
      <Link to={`/s/${s.id}`} data-testid={`show-link-${s.id}`} className="group block">
        <div className="flex items-center gap-2">
          {featured && <span className="bg-accent px-1.5 py-0.5 font-mono text-[10px] font-bold uppercase text-black">Live</span>}
          <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
            {fmtDate(s.created_at)} · {s.clip_count} clips{s.is_video ? " · video" : ""}
          </span>
        </div>
        <p className={`mt-1 font-display tracking-wide group-hover:text-accent ${featured ? "text-2xl" : "text-lg"}`}>{s.title}</p>
        {tags.length > 0 && (
          <div className="mt-2 flex flex-wrap gap-1.5">
            {tags.map((t) => <span key={t} className="bg-primary/15 px-1.5 py-0.5 font-mono text-[10px] uppercase tracking-wider text-primary">{t}</span>)}
          </div>
        )}
        {featured && s.teasers?.length > 0 && (
          <p className="mt-2 line-clamp-1 font-body text-xs italic text-muted-foreground">Teasers: {s.teasers.join(" · ")}</p>
        )}
        <p className="mt-3 font-mono text-[11px] uppercase tracking-widest text-accent">
          {featured ? "▶ Watch the full show" : "▶ Open full episode"} →
        </p>
      </Link>
    </div>
  );
}

function DefaultBrowse({ data }) {
  const tiktok = (data.shows || []).filter((s) => s.era === "tiktok");
  const archive = (data.shows || []).filter((s) => s.era !== "tiktok");
  return (
    <div className="space-y-12">
      {tiktok.length > 0 && (
        <div data-testid="featured-episodes">
          <h2 className="font-display text-3xl tracking-wide text-accent">Full Episodes — Late Night with Barth &amp; Friends</h2>
          <p className="mt-1 font-body text-sm text-muted-foreground">Tap any show to watch the whole episode, jump to chapters, search moments, and grab clips.</p>
          <div className="mt-4 grid gap-4 sm:grid-cols-2">
            {tiktok.map((s) => <EpisodeCard key={s.id} s={s} featured />)}
          </div>
        </div>
      )}
      {archive.length > 0 && (
        <div data-testid="archive-episodes">
          <h2 className="font-display text-2xl tracking-wide text-muted-foreground">Full Episodes — Barth and Friends (Before TikTok)</h2>
          <p className="mt-1 font-body text-xs text-muted-foreground">The early archive — tap to open the full show.</p>
          <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {archive.slice(0, 60).map((s) => <EpisodeCard key={s.id} s={s} />)}
          </div>
        </div>
      )}
      {!isBrandedHost() && data.creator?.destination_url && (
        <div className="border border-accent/30 bg-accent/[0.05] p-6 text-center" data-testid="listen-all-cta">
          <p className="font-display text-xl">Want every full show in one place?</p>
          <p className="mt-1 font-body text-sm text-muted-foreground">Listen to the whole catalog at {data.creator.destination_label || data.creator.destination_url.replace(/^https?:\/\//, "")}</p>
          <a href={data.creator.destination_url} target="_blank" rel="noopener noreferrer"
             className="mt-4 inline-block bg-accent px-6 py-3 font-body text-sm font-semibold text-black transition-transform hover:-translate-y-0.5">
            Go to {data.creator.destination_label || "the site"} →
          </a>
        </div>
      )}
    </div>
  );
}
